import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from "@mui/material";
import {
  EditOutlined,
  PersonOutline,
  FitnessCenter,
  Settings,
  Restaurant,
  PlaylistAddCheck as ChecklistIcon,
  ToggleOn,
  ToggleOff,
  DeleteOutline,
  StickyNote2 as NoteIcon,
  EmojiEvents as TargetIcon,
  Lock as LockIcon,
  AssignmentTurnedIn,
  Send as SendIcon,
} from "@mui/icons-material";
import EditClientDialog from "./EditClientDialog";
import ChangeTrainerDialog from "./ChangeTrainerDialog";
import ChangeProgramDialog from "../ChnageProgramDialog";
import NoteDialog from "./NoteDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";
import UserSettingsDialog from "./UserSettingsDialog";
import CreateCheckInDialog from "../check-ins/CreateCheckInDialog";
import SendPushDialog from "../dashboard/coach/SendPushDialog";
import { ConfirmDialog } from "../ui";
import { colors, borders, shadows } from "../ui/colors";
import {
  enableClients,
  disableClients,
  deleteTrainee,
  updateTrainer,
  saveClientNote,
  saveClientTarget,
  resetTraineePassword,
} from "../../services/TraineeService";
import { changeProgram } from "../../services/PlanSerice";
import { createCheckIn } from "../../services/CheckInService";
import { getErrorMessage } from "../../enums/ErrorMessages";
import { useMessageContext } from "../../contexts/MessageContext";

/**
 * Reusable action menu for trainee row / client page header.
 * Receives a minimal trainee shape ({ id, name, enabled, companyId }) and
 * an anchorEl. Renders the menu + all dialogs it controls. Notifies the
 * caller via `onChanged` when something mutates (toggle/edit/etc) so they
 * can refetch. When rendered from a trainee page, pass `onDeleted` to be
 * called instead of `onChanged` after a successful delete (e.g. to redirect
 * to the clients list, since the trainee no longer exists).
 */
const ClientActionMenu = ({ trainee, anchorEl, onClose, onChanged, onDeleted }) => {
  const navigate = useNavigate();
  const { notificationHandler } = useMessageContext();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [changeTrainerOpen, setChangeTrainerOpen] = useState(false);
  const [changeProgramOpen, setChangeProgramOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addTargetOpen, setAddTargetOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);
  const [requestCheckInOpen, setRequestCheckInOpen] = useState(false);
  const [sendPushOpen, setSendPushOpen] = useState(false);

  if (!trainee) return null;

  const close = () => onClose?.();
  const fired = () => { onChanged?.(); };

  const handleEnableDisable = () => {
    const action = trainee.enabled ? disableClients : enableClients;
    const msg = trainee.enabled ? "מתאמן נחסם בהצלחה" : "מתאמן הופעל בהצלחה";
    action([trainee.id])
      .then(() => notificationHandler.success(msg))
      .then(fired)
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
    close();
  };

  const handleDelete = () => {
    deleteTrainee(trainee.id)
      .then(() => {
        notificationHandler.success("מתאמן נמחק");
        setDeleteOpen(false);
        // When rendered from a trainee page, redirect to the clients list
        // (the trainee no longer exists); otherwise fall back to refetch.
        if (onDeleted) onDeleted();
        else fired();
      })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const handleChangeTrainer = (trainerId) => {
    updateTrainer(trainee.id, trainerId)
      .then(() => { setChangeTrainerOpen(false); fired(); })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const handleChangeProgram = (templateId, includeRmWeek = false, advancedSettings = null) => {
    changeProgram(trainee.id, templateId, includeRmWeek, advancedSettings)
      .then(() => notificationHandler.success("תוכנית שונתה בהצלחה"))
      .then(() => { setChangeProgramOpen(false); fired(); })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const handleAddNote = (note) => {
    saveClientNote({ traineeId: trainee.id, message: note.message })
      .then(() => { setAddNoteOpen(false); fired(); notificationHandler.success("הערה נוספה בהצלחה"); })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const handleAddTarget = (target) => {
    saveClientTarget({ traineeId: trainee.id, message: target.message })
      .then(() => { setAddTargetOpen(false); fired(); notificationHandler.success("מטרה נוספה בהצלחה"); })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const handleChangePassword = (password, forcePasswordChange) => {
    resetTraineePassword(trainee.id, password, forcePasswordChange)
      .then(() => { setChangePasswordOpen(false); notificationHandler.success("סיסמה שונתה בהצלחה"); })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const handleCreateCheckIn = async (data) => {
    await createCheckIn(data);
    notificationHandler.success("צ'ק-אין נשלח בהצלחה");
    setRequestCheckInOpen(false);
    fired();
  };

  const items = [
    // Re-enters the creation wizard at its configuration half, so the steps a coach skipped
    // (goals, menu, program, check-ins) can be completed in one pass instead of four screens.
    { label: "השלמת הגדרות", icon: <ChecklistIcon fontSize="small" />, onClick: () => { navigate(`/app/clients/${trainee.id}/setup`); close(); } },
    { divider: true },
    { label: "ערוך פרופיל", icon: <EditOutlined fontSize="small" />, onClick: () => { setEditOpen(true); close(); } },
    { label: "שנה מאמן", icon: <PersonOutline fontSize="small" />, onClick: () => { setChangeTrainerOpen(true); close(); } },
    { label: "שנה תוכנית", icon: <FitnessCenter fontSize="small" />, onClick: () => { setChangeProgramOpen(true); close(); } },
    { label: "שנה סיסמה", icon: <LockIcon fontSize="small" />, onClick: () => { setChangePasswordOpen(true); close(); } },
    { label: "בקש צ'ק-אין", icon: <AssignmentTurnedIn fontSize="small" />, onClick: () => { setRequestCheckInOpen(true); close(); } },
    { label: "שלח התראה", icon: <SendIcon fontSize="small" />, onClick: () => { setSendPushOpen(true); close(); } },
    { label: "הגדרות מתאמן", icon: <Settings fontSize="small" />, onClick: () => { setUserSettingsOpen(true); close(); } },
    { label: "תזונה", icon: <Restaurant fontSize="small" />, onClick: () => { navigate(`/app/clients/${trainee.id}/nutrition`); close(); } },
    { divider: true },
    { label: "הוסף הערה", icon: <NoteIcon fontSize="small" />, onClick: () => { setAddNoteOpen(true); close(); } },
    { label: "הוסף מטרה", icon: <TargetIcon fontSize="small" />, onClick: () => { setAddTargetOpen(true); close(); } },
    { divider: true },
    {
      label: trainee.enabled ? "חסום מתאמן" : "הפעל מתאמן",
      icon: trainee.enabled ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />,
      onClick: handleEnableDisable,
      color: trainee.enabled ? colors.error : colors.success,
    },
    {
      label: "מחק לקוח",
      icon: <DeleteOutline fontSize="small" />,
      onClick: () => { setDeleteOpen(true); close(); },
      color: colors.error,
    },
  ];

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: {
          borderRadius: borders.radius.lg, border: borders.default,
          boxShadow: shadows.dropdown, mt: 0.5, minWidth: 200,
        } }}
      >
        {items.map((item, i) =>
          item.divider ? (
            <Divider key={`d-${i}`} sx={{ my: 0.5 }} />
          ) : (
            <MenuItem
              key={item.label}
              onClick={item.onClick}
              sx={{
                fontSize: "14px", fontWeight: 500, py: 1.25, px: 2,
                mx: 0.75, borderRadius: borders.radius.sm,
                color: item.color || colors.textPrimary,
                "&:hover": {
                  bgcolor: item.color ? `${item.color}10` : colors.primarySubtle,
                  color: item.color || colors.primary,
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>{item.icon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}>{item.label}</ListItemText>
            </MenuItem>
          )
        )}
      </Menu>

      <EditClientDialog
        navigate={navigate}
        reload={fired}
        close={() => setEditOpen(false)}
        isOpen={editOpen}
        trainee={trainee}
      />
      <ChangeTrainerDialog
        companyId={trainee.companyId}
        handleSubmit={handleChangeTrainer}
        trainer={null}
        close={() => setChangeTrainerOpen(false)}
        isOpen={changeTrainerOpen}
      />
      <ChangeProgramDialog
        isOpen={changeProgramOpen}
        close={() => setChangeProgramOpen(false)}
        handleChangeProgram={handleChangeProgram}
        trainee={trainee}
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="מחיקת לקוח"
        description={`האם למחוק את ${trainee.name}? פעולה זו אינה ניתנת לביטול.`}
        confirmText="מחק"
        variant="danger"
      />
      <NoteDialog
        isOpen={addNoteOpen}
        close={() => setAddNoteOpen(false)}
        approve={handleAddNote}
        initValue={{}}
        title="הוסף הערה"
      />
      <NoteDialog
        isOpen={addTargetOpen}
        close={() => setAddTargetOpen(false)}
        approve={handleAddTarget}
        initValue={{}}
        title="הוסף מטרה"
      />
      <ChangePasswordDialog
        isOpen={changePasswordOpen}
        close={() => setChangePasswordOpen(false)}
        onSubmit={handleChangePassword}
      />
      <UserSettingsDialog
        isOpen={userSettingsOpen}
        close={() => setUserSettingsOpen(false)}
        client={trainee}
        onSaved={fired}
      />
      <CreateCheckInDialog
        open={requestCheckInOpen}
        onClose={() => setRequestCheckInOpen(false)}
        onSubmit={handleCreateCheckIn}
        preSelectedTrainee={{ id: trainee.id, name: trainee.name }}
      />
      <SendPushDialog
        open={sendPushOpen}
        onClose={() => setSendPushOpen(false)}
        trainee={{ traineeId: trainee.id, traineeName: trainee.name }}
        onSent={(err) => {
          if (err) notificationHandler.error("שליחה נכשלה");
          else notificationHandler.success(`התראה נשלחה ל-${trainee.name}`);
        }}
      />
    </>
  );
};

export default ClientActionMenu;
